// RTK slice action for simple state update
import { updateRiskPriority } from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';

export class RiskEvaluationService {
  constructor(dispatch, modelId, assessmentUuid, contextData = {}, impactCategories = [], liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.contextData = contextData;
    this.impactCategories = impactCategories; // Template-based impact categories (replaces legacy themes)
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeEvaluation(areaName, riskFormData, riskModel, riskFormTaxonomy, progressCallback) {
    // Handle legacy callers that don't pass riskFormTaxonomy (4 args instead of 5)
    if (typeof riskFormTaxonomy === 'function') {
      progressCallback = riskFormTaxonomy;
      riskFormTaxonomy = null;
    }

    try {
      if (progressCallback) progressCallback('evaluation');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state - check RTK assessment slice first, fallback to legacy
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Assessment-centric approach: Read from RTK slice (riskItems), fallback to legacy (risks)
      // RTK slice uses: state.riskItems.byAssessment[assessmentUuid][areaName].areaRisks
      // Legacy uses: state.risks.byAssessment[assessmentUuid].modelRisks[areaName].areaRisks
      const rtkRisksState = currentState.riskItems?.byAssessment?.[this.assessmentUuid];
      const legacyRisksState = currentState.risks?.byAssessment?.[this.assessmentUuid];

      // Get area risks - RTK first (with modelRisks wrapper), then legacy fallback
      const rtkAreaRisks = rtkRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const legacyAreaRisks = legacyRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const areaRisks = Object.keys(rtkAreaRisks).length > 0 ? rtkAreaRisks : legacyAreaRisks;

      // Log state source
      console.log(`[RiskEvaluationService] 🔍 State source for area: ${areaName}`);
      console.log(`[RiskEvaluationService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[RiskEvaluationService] Available risk sources:`, Object.keys(areaRisks));
      console.log(`[RiskEvaluationService] riskFormTaxonomy provided:`, !!riskFormTaxonomy);
      
      // Find risks that need evaluation
      const risksToEvaluate = [];
      
      console.log(`[RiskEvaluationService] 🔍 Searching for unevaluated risks in area: ${areaName}`);
      console.log(`[RiskEvaluationService] Available risk sources:`, Object.keys(areaRisks));
      
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        console.log(`[RiskEvaluationService] Checking source "${sourceName}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[RiskEvaluationService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && !risk.priority) {
              console.log(`[RiskEvaluationService] ✅ FOUND unevaluated risk: "${risk.riskName}"`);
              risksToEvaluate.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });

      // Use FastTrack AI evaluation with context-aware analysis
      if (risksToEvaluate.length > 0) {
        console.log(`[RiskEvaluationService] Evaluating ${risksToEvaluate.length} risks using FastTrack approach`);
        
        let evaluatedCount = 0;
        for (const risk of risksToEvaluate) {
          try {
            // Get causes data for this risk's source
            // Use riskFormTaxonomy (template structure) which has riskSources[].causes
            // NOT riskFormData (saved data) which has sectionRisks
            const taxonomyAreaData = riskFormTaxonomy?.[areaName];
            const savedAreaData = riskFormData?.[areaName];
            const sourceCauses = this.extractCausesForRisk(risk, taxonomyAreaData, savedAreaData);

            console.log(`[RiskEvaluationService] Causes for ${risk.riskName}:`, {
              fromTaxonomy: !!taxonomyAreaData,
              causesCount: sourceCauses?.length || 0
            });
            
            // Call FastTrack evaluation with full context
            // Pass template-based impact categories (not legacy DB themes)
            const evalResponse = await authFetch('/.netlify/functions/fastTrackRiskEvaluation', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                risk,
                causes: sourceCauses,
                contextData: this.contextData,
                impactCategories: this.impactCategories, // Template-based impact categories
                modelUuid: this.modelId,
                assessmentUuid: this.assessmentUuid,
                riskArea: areaName,
                sourceName: risk.sourceName
              })
            });
            
            if (evalResponse.ok) {
              const evalData = await evalResponse.json();
              if (evalData.fastTrackEvaluation) {
                const evaluation = evalData.fastTrackEvaluation;
                const priority = evaluation.priority?.toLowerCase() || 'moderate';
                const rationale = evaluation.rationale || '';
                
                console.log(`[RiskEvaluationService] FastTrack evaluated ${risk.riskName}: ${priority}`);
                
                // Save the FastTrack evaluation using the same pattern as co-pilot
                await this.saveFastTrackEvaluation(risk, evaluation, priority, rationale, areaName);
                
                evaluatedCount++;
                
                // LIVE UPDATE: Stream each risk evaluation as it completes
                if (this.liveUpdateCallback) {
                  this.liveUpdateCallback('riskEvaluation', {
                    riskName: risk.riskName,
                    priority: priority,
                    rationale: rationale,
                    confidence: evaluation.confidence,
                    themes: evaluation.fastTrackData?.selectedThemes?.length || 0,
                    evaluatedCount,
                    totalRisks: risksToEvaluate.length
                  });
                }
              }
            } else {
              console.error(`[RiskEvaluationService] FastTrack evaluation failed for ${risk.riskName}:`, await evalResponse.text());
            }
          } catch (evalError) {
            console.error(`[RiskEvaluationService] Risk evaluation failed for ${risk.riskName}:`, evalError);
          }
        }
      }

      return {
        evaluatedRisks: risksToEvaluate.length,
        areaName
      };

    } catch (error) {
      console.error('[RiskEvaluationService] Evaluation failed:', error);
      throw error;
    }
  }

  // Extract causes data for a specific risk's source
  // taxonomyAreaData = riskFormTaxonomy[areaName] - has riskSources[].causes (template structure)
  // savedAreaData = riskFormData[areaName] - has sectionRisks (saved assessment data)
  extractCausesForRisk(risk, taxonomyAreaData, savedAreaData) {
    try {
      // First try: from taxonomy riskSources array (this is the primary source)
      const fromRiskSources = taxonomyAreaData?.riskSources?.find(src => src.name === risk.sourceName)?.causes;
      if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
        console.log(`[RiskEvaluationService] Found causes from taxonomy riskSources for: ${risk.sourceName}`);
        return Object.entries(fromRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      // Second try: from taxonomy causesData if it exists
      const fromTaxonomyCausesData = taxonomyAreaData?.causesData?.[risk.sourceName];
      if (fromTaxonomyCausesData && Object.keys(fromTaxonomyCausesData).length > 0) {
        console.log(`[RiskEvaluationService] Found causes from taxonomy causesData for: ${risk.sourceName}`);
        return Object.entries(fromTaxonomyCausesData).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      // Third try: from saved data riskSources (fallback)
      const fromSavedRiskSources = savedAreaData?.riskSources?.find(src => src.name === risk.sourceName)?.causes;
      if (fromSavedRiskSources && Object.keys(fromSavedRiskSources).length > 0) {
        console.log(`[RiskEvaluationService] Found causes from saved riskSources for: ${risk.sourceName}`);
        return Object.entries(fromSavedRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      console.log(`[RiskEvaluationService] No causes found for risk source: ${risk.sourceName}`);
      return [];
    } catch (error) {
      console.error('[RiskEvaluationService] Error extracting causes:', error);
      return [];
    }
  }

  // Save FastTrack evaluation results
  async saveFastTrackEvaluation(risk, evaluation, priority, rationale, areaName) {
    try {
      // STEP 1: Save theme evaluations if themes exist (legacy theme-based flow)
      if (evaluation.fastTrackData?.selectedThemes?.length > 0) {
        const savePromises = evaluation.fastTrackData.selectedThemes.map(theme =>
          authFetch('/.netlify/functions/saveRiskEvaluation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              modelUuid: this.modelId,
              assessmentUuid: this.assessmentUuid,
              riskName: risk.riskName,
              riskId: risk.riskId,
              themeId: theme.themeId,
              riskArea: areaName,
              priority: priority,
              rationale: rationale,
              data: theme.evaluation?.impact || theme.highLevelAssessment || {},
            }),
          })
        );

        // Wait for all theme saves to complete
        const responses = await Promise.all(savePromises);
        const failedSaves = responses.filter(response => !response.ok);

        if (failedSaves.length > 0) {
          console.error(`[RiskEvaluationService] Failed to save ${failedSaves.length} theme evaluations for ${risk.riskName}`);
        } else {
          console.log(`[RiskEvaluationService] Successfully saved ${evaluation.fastTrackData.selectedThemes.length} theme evaluations for ${risk.riskName}`);
        }
      }

      // STEP 2: ALWAYS update Redux state with priority and rationale (regardless of themes)
      this.dispatch(
        updateRiskPriority({
          assessmentUuid: this.assessmentUuid,
          sectionName: areaName,
          category: risk.sourceName,
          riskId: risk.riskId,
          priority,
          rationale,
        })
      );

      console.log(`[RiskEvaluationService] Priority saved for ${risk.riskName}: ${priority}`);

    } catch (error) {
      console.error(`[RiskEvaluationService] Error saving FastTrack evaluation for ${risk.riskName}:`, error);
    }
  }
}